import React, { useState, useEffect } from "react";
import { Button, Form, Table, Modal, Alert } from "react-bootstrap";
import { HttpResult } from "./types";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const HttpStatusTool: React.FC = () => {
  const [urls, setUrls] = useState<string>("");
  const [urlList, setUrlList] = useState<string[]>([]);
  const [results, setResults] = useState<HttpResult[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalResponse, setModalResponse] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const normalizeUrl = (url: string): string => {
    return url.replace(/\/+$/, "");
  };

  const getBaseUrl = (url: string): string => {
    const normalized = normalizeUrl(url);
    return normalized.replace(/^https?:\/\//, "");
  };

  const updateUrlList = (inputUrls: string) => {
    const newUrlList = inputUrls
      .split(",")
      .map((url) => url.trim())
      .filter((url) => url);
    setUrlList(newUrlList);
    return newUrlList;
  };

  const handleCheck = async () => {
    setLoading(true);
    setError("");
    setResults([]);

    const newUrlList = updateUrlList(urls);
    if (newUrlList.length === 0) {
      setError("Please enter at least one valid URL.");
      setLoading(false);
      return;
    }

    const formattedUrls: string[] = [];
    const seenUrls = new Set<string>();
    newUrlList.forEach((url: string) => {
      const normalized = normalizeUrl(url);
      if (seenUrls.has(normalized)) return;
      seenUrls.add(normalized);

      if (url.match(/^https?:\/\//)) {
        formattedUrls.push(normalized);
      } else {
        formattedUrls.push(normalizeUrl(`http://${url}`));
        formattedUrls.push(normalizeUrl(`https://${url}`));
      }
    });

    try {
      const response = await fetch(
        "https://api.performancedashboard.io/api/httpChecker",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ urls: formattedUrls }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error! Status: ${response.status}`);
      }

      const { data } = await response.json();
      const newResults: HttpResult[] = [];
      const processedUrls = new Set<string>();

      for (const result of data) {
        const normalizedUrl = normalizeUrl(result.url);
        if (processedUrls.has(normalizedUrl)) continue;
        processedUrls.add(normalizedUrl);

        if (result.error) {
          newResults.push({
            url: result.url,
            code: "Error",
            status: "Failed",
            final_url: null,
            response: result.error,
          });
        } else {
          let statusCode = result.status || result.statusCode || 0;
          const statusText =
            result.statusText ||
            (statusCode >= 200 && statusCode < 300 ? "OK" : "Unknown");
          let responseData = `HTTP/1.1 ${statusCode} ${statusText}\n`;

          if (result.headers) {
            Object.entries(result.headers).forEach(([key, value]) => {
              responseData += `${key}: ${value}\n`;
            });
          }

          let finalUrl =
            result.finalUrl ||
            (result.headers && result.headers.location) ||
            result.url;
          let normalizedFinalUrl = normalizeUrl(finalUrl);

          newResults.push({
            url: result.url,
            code: statusCode.toString(),
            status: statusText,
            final_url: finalUrl,
            response: responseData,
          });

          let currentUrl = finalUrl;
          let currentNormalizedUrl = normalizedFinalUrl;
          while (
            statusCode >= 300 &&
            statusCode < 400 &&
            currentNormalizedUrl !== normalizedUrl &&
            !processedUrls.has(currentNormalizedUrl)
          ) {
            processedUrls.add(currentNormalizedUrl);

            try {
              const redirectResponse = await fetch(
                "https://api.performancedashboard.io/api/httpChecker",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ urls: [currentUrl] }),
                }
              );

              if (!redirectResponse.ok) {
                throw new Error(
                  `API error on redirect! Status: ${redirectResponse.status}`
                );
              }

              const { data: redirectData } = await redirectResponse.json();
              const redirectResult = redirectData[0];

              if (redirectResult.error) {
                newResults.push({
                  url: redirectResult.url,
                  code: "Error",
                  status: "Failed",
                  final_url: null,
                  response: redirectResult.error,
                });
                break;
              } else {
                const redirectStatusCode =
                  redirectResult.status || redirectResult.statusCode || 0;
                const redirectStatusText =
                  redirectResult.statusText ||
                  (redirectStatusCode >= 200 && redirectStatusCode < 300
                    ? "OK"
                    : "Unknown");
                let redirectResponseData = `HTTP/1.1 ${redirectStatusCode} ${redirectStatusText}\n`;

                if (redirectResult.headers) {
                  Object.entries(redirectResult.headers).forEach(
                    ([key, value]) => {
                      redirectResponseData += `${key}: ${value}\n`;
                    }
                  );
                }

                currentUrl =
                  redirectResult.finalUrl ||
                  (redirectResult.headers && redirectResult.headers.location) ||
                  redirectResult.url;
                currentNormalizedUrl = normalizeUrl(currentUrl);

                newResults.push({
                  url: redirectResult.url,
                  code: redirectStatusCode.toString(),
                  status: redirectStatusText,
                  final_url: currentUrl,
                  response: redirectResponseData,
                });

                statusCode = redirectStatusCode;
              }
            } catch (redirectErr: any) {
              console.error(
                `Failed to fetch redirect URL ${currentUrl}: ${redirectErr.message}`
              );
              newResults.push({
                url: currentUrl,
                code: "Error",
                status: "Failed",
                final_url: null,
                response: `Error fetching redirect: ${redirectErr.message}`,
              });
              break;
            }
          }
        }
      }

      setResults(newResults);
      if (newResults.length === 0) {
        setError("No valid responses received from the API.");
      }
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (err: any) {
      console.error("API request failed:", err.message);
      setError(`Failed to fetch results: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const showFullResponse = (response: string) => {
    const cleanedResponse = decodeURIComponent(response)
      .replace(/&/g, "&")
      .replace(/</g, "<")
      .replace(/>/g, ">")
      .replace(/[\r\n]+/g, "\n");
    setModalResponse(cleanedResponse);
    setShowModal(true);
  };

  const groupedResults: { [key: string]: HttpResult[] } = {};
  urlList.forEach((url: string) => {
    const baseUrl = url.match(/^https?:\/\//) ? url : `http://${url}`;
    groupedResults[baseUrl] = [];
  });

  results.forEach((result) => {
    let matchedBaseUrl: string | undefined;
    const normalizedResultBase = getBaseUrl(result.url);

    urlList.forEach((url: string) => {
      const baseUrl = url.match(/^https?:\/\//) ? url : `http://${url}`;
      const normalizedInputBase = getBaseUrl(baseUrl);

      if (
        normalizedResultBase.includes(normalizedInputBase) ||
        normalizedInputBase.includes(normalizedResultBase)
      ) {
        matchedBaseUrl = baseUrl;
      }
    });

    const groupKey = matchedBaseUrl || result.url;
    if (!groupedResults[groupKey]) {
      groupedResults[groupKey] = [];
    }
    groupedResults[groupKey].push(result);
  });

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [error, groupedResults]);

  return (
    <div
      className={`http-status-container ${
        !isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
      }`}
      style={{
        fontFamily: "Arial, sans-serif",
        margin: "0 auto",
        maxWidth: "1000px",
        textAlign: "center",
        padding: "0px",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          padding: "20px",
          marginBottom: "20px",
        }}
      >
        {!isInIframe && (
          <ToolsHeader
            title="HTTP Status Tool"
            description="Effortlessly check any domain’s HTTP status and gain valuable insights for better decisions."
          />
        )}
        <Form>
          <Form.Group controlId="http-urls">
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter URLs separated by commas (e.g., coozmoo.com, https://google.com)"
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              style={{
                resize: "none",
                minHeight: "80px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                padding: "10px",
                width: "100%",
                marginBottom: "15px",
                boxShadow: "none",
                fontSize: "16px",
              }}
            />
          </Form.Group>
          <Button
            onClick={handleCheck}
            disabled={loading}
            style={{
              backgroundColor: "#8CC540",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "10px 20px",
              fontWeight: 500,
              transition: "background-color 0.3s ease",
              fontSize: "16px",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#5c3dff")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#8CC540")
            }
          >
            {loading ? "Checking..." : "Check"}
          </Button>
        </Form>
      </div>

      {error && (
        <Alert
          variant="danger"
          style={{
            marginTop: "15px",
            padding: "10px",
            borderRadius: "5px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
            textAlign: "center",
            fontSize: "14px",
          }}
        >
          {error}
        </Alert>
      )}

      {Object.keys(groupedResults).length > 0 && (
        <div
          style={{
            overflowX: "auto",
            marginTop: "20px",
          }}
        >
          {Object.keys(groupedResults).map(
            (baseUrl, index) =>
              groupedResults[baseUrl].length > 0 && (
                <div key={index} style={{ marginBottom: "20px" }}>
                  <h3
                    style={{
                      textAlign: "left",
                      color: "#333",
                      fontWeight: 500,
                      marginBottom: "10px",
                      fontSize: "18px",
                    }}
                  >
                    {baseUrl}
                  </h3>
                  <Table
                    striped
                    bordered
                    hover
                    style={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      overflow: "hidden",
                      borderCollapse: "collapse",
                      minWidth: "100%",
                      fontSize: "14px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            backgroundColor: "#f8f9fa",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Code
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            backgroundColor: "#f8f9fa",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Status
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            backgroundColor: "#f8f9fa",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Host
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            backgroundColor: "#f8f9fa",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Full Response
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedResults[baseUrl].map((result, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              textAlign: "center",
                              padding: "8px",
                              verticalAlign: "middle",
                            }}
                          >
                            <span
                              style={{
                                color: "#fff",
                                backgroundColor:
                                  result.code === "200" ? "green" : "#911111",
                                padding: "5px 10px",
                                borderRadius: "20px",
                                display: "inline-block",
                                fontWeight: "bold",
                                fontSize: "12px",
                              }}
                            >
                              {result.code}
                            </span>
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              padding: "8px",
                              verticalAlign: "middle",
                              fontSize: "14px",
                            }}
                          >
                            {result.status}
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              padding: "8px",
                              verticalAlign: "middle",
                              fontSize: "14px",
                              wordBreak: "break-all",
                            }}
                          >
                            {result.url}
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              padding: "8px",
                              verticalAlign: "middle",
                            }}
                          >
                            <Button
                              variant="link"
                              onClick={() => showFullResponse(result.response)}
                              style={{
                                color: "#007bff",
                                textDecoration: "underline",
                                padding: "0",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: "14px",
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )
          )}
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Full Response</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              backgroundColor: "#f0f0f0",
              padding: "15px",
              borderRadius: "5px",
              fontFamily: "monospace",
              maxHeight: "60vh",
              overflowY: "auto",
              fontSize: "14px",
            }}
          >
            {modalResponse}
          </pre>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default HttpStatusTool;